import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DefaultRxTxValue, ErrorMessages, Regex } from "../../utils/appConstants";
import Button from "../DesignSystem/button";
import AddBandwidthForm from "./form";
import { AuthRoutes } from "../../enums";
import AddPortService from "../../services/addPort.service";
import DashboardService from "../../services/dashboard.service";
import ErrorInfoIcon from "../../assets/images/error-info-icon.svg";
import { ErrorCodeMessageMapping, ErrorCodesMapping, SuccessMessage, ErrorMessages as Errors } from "../../utils/apiConstants";
import SuccessInfoIcon from "../../assets/images/success-toast-checkmark-icon.svg";
import { useIsMounted } from "../../utils/customHooks";
import { useGroup } from "../ManagePorts/GroupOperations/useGroup";

const AddBandwidth: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as any;
  const [selectedNeNetworkElement, setSelectedNeNetworkElement] = useState<any>((state && state.nearendNe) || {});
  const [selectedFeNetworkElement, setSelectedFeNetworkElement] = useState<any>((state && state.farendNe) || {});
  const [neInput, setNeInput] = useState<string>((state && state.nearendNe && state.nearendNe.ne_name) || "");
  const [feInput, setFeInput] = useState<string>((state && state.farendNE && state.farendNe.ne_name) || "");
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [neNetworkElements, setNeNetworkElements] = useState<any[]>([]);
  const [feNetworkElements, setFeNetworkElements] = useState<any[]>([]);
  const [portTypes, setPortTypes] = useState<any[]>([]);
  const [nePortsInfo, setNePortsInfo] = useState<any[]>([]);
  const [fePortsInfo, setFePortsInfo] = useState<any[]>([]);
  const [linkPortsInfo, setLinkPortsInfo] = useState<any[]>([]);
  const [bandwidthDescription, setBandwidthDescription] = useState<string>("");
  const [fePortDescription, setFePortDescription] = useState<string>("");
  const [nePortDescription, setNePortDescription] = useState<string>("");
  const [noOfLinksError, setNoOfLinksError] = useState<string>("");
  const [noOfLinksApiError, setNoOfLinksApiError] = useState<string>("");
  const [noOfLinksToAdd, setNoOfLinksToAdd] = useState<number | string>(1);
  const [addBandwidthLoading, setAddBandwidthLoading] = useState<boolean>(false);
  const [tempPortTypeId, setTempPortTypeId] = useState<any>("");
  const [verifyNearendPorts, setVerifyNearendPorts] = useState<boolean>(false);
  const [verifyFarendPorts, setVerifyFarendPorts] = useState<boolean>(false);
  const [nePortPrefix, setNePortPrefix] = useState<string>("Port");
  const [fePortPrefix, setFePortPrefix] = useState<string>("Port");
  const [linkPrefix, setLinkPrefix] = useState<string>("Wave");

  const isMounted = useIsMounted();
  const neUseGroupProps = useGroup(selectedNeNetworkElement.id, { isMounted, isBandwidth: true, lowerDrawer: false }, null, null, {
    DefaultGroup: selectedFeNetworkElement ? selectedFeNetworkElement.ne_name : null,
  });
  const feUseGroupProps = useGroup(selectedFeNetworkElement.id, { isMounted, isBandwidth: true, lowerDrawer: false }, null, null, {
    DefaultGroup: selectedNeNetworkElement ? selectedNeNetworkElement.ne_name : null,
  });

  const resetPortInfo = useCallback(
    (portPrefix: string, noPorts: string | number, selectedPortType: any, isValidFunction: (_: string) => boolean, ensureValid: boolean = false) => {
      const tempPortsInfo = [];
      let count = 0;

      for (let i = 1; i <= Number(noPorts); i++) {
        let portName: string = "";

        do {
          count++;
          portName = portPrefix ? [portPrefix, count].join(" ") : count.toString();
        } while (ensureValid && !isValidFunction(portName) && count < 272);

        tempPortsInfo.push({
          id: i,
          portName,
          rx: DefaultRxTxValue,
          tx: DefaultRxTxValue,
          portTypeId: selectedPortType,
        });
      }
      return tempPortsInfo;
    },

    []
  );

  const resetNePortInfo = useCallback(
    (portPrefix: string, noPorts: string | number, selectedPortType: any, ensureValid: boolean) => {
      return resetPortInfo(portPrefix, noPorts, selectedPortType, neUseGroupProps.GroupDropdownProps.IsPortNameValid, ensureValid);
    },
    [neUseGroupProps.GroupDropdownProps.IsPortNameValid]
  );
  const resetFePortInfo = useCallback(
    (portPrefix: string, noPorts: string | number, selectedPortType: any, ensureValid: boolean) => {
      return resetPortInfo(portPrefix, noPorts, selectedPortType, feUseGroupProps.GroupDropdownProps.IsPortNameValid, ensureValid);
    },
    [feUseGroupProps.GroupDropdownProps.IsPortNameValid]
  );

  const checkValidLink = useCallback(() => {
    // TODO: implement
    return true;
  }, []);

  const resetLinkPortInfo = useCallback(
    (linkPrefix: string, noLinks: string | number, selectedLinkType: any, ensureValid: boolean) => {
      return resetPortInfo(linkPrefix, noLinks, selectedLinkType, checkValidLink, ensureValid);
    },
    [checkValidLink, resetPortInfo]
  );

  useEffect(() => {
    setNePortsInfo(resetNePortInfo(nePortPrefix, noOfLinksToAdd, tempPortTypeId, true));
  }, [resetNePortInfo]);

  useEffect(() => {
    setFePortsInfo(resetFePortInfo(fePortPrefix, noOfLinksToAdd, tempPortTypeId, true));
  }, [resetFePortInfo]);

  useEffect(() => {
    setLinkPortsInfo(resetLinkPortInfo(fePortPrefix, noOfLinksToAdd, tempPortTypeId, true));
  }, [resetLinkPortInfo]);

  const fetchNetworkElementsAndPortTypes = useCallback(() => {
    AddPortService.getNetworkElements()
      .then((response: any) => {
        AddPortService.getPortTypes()
          .then((responsePortTypes: any) => {
            if (!isMounted) return;
            setPortTypes(responsePortTypes.data || []);
            setNetworkElements(response.data || []);
            let nearendIndex: any = "";
            let nearendId = -1;
            let farendIndex: any = "";
            let farendId = -1;
            if (selectedNeNetworkElement) {
              nearendId = Number(selectedNeNetworkElement.id);
            }
            if (selectedFeNetworkElement) {
              farendId = Number(selectedFeNetworkElement.id);
            }
            response.data.filter((item: any, index: number) => {
              if (nearendId === item.id) {
                nearendIndex = index;
              }
              if (farendId === item.id) {
                farendIndex = index;
              }
              return 0;
            });

            setNeNetworkElements(response.data.filter((item: any, index: number) => index !== farendIndex));
            setFeNetworkElements(response.data.filter((item: any, index: number) => index !== nearendIndex));
            setNePortsInfo(resetNePortInfo(nePortPrefix, noOfLinksToAdd, tempPortTypeId, true));
            setFePortsInfo(resetFePortInfo(fePortPrefix, noOfLinksToAdd, tempPortTypeId, true));
            setLinkPortsInfo(resetLinkPortInfo(linkPrefix, noOfLinksToAdd, tempPortTypeId, true));
            setTempPortTypeId(responsePortTypes.data[0]?.id);
          })
          .catch(() => {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            setPortTypes([]);
          });
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setNetworkElements([]);
      });
  }, [
    selectedNeNetworkElement,
    selectedFeNetworkElement,
    setLinkPortsInfo,
    setNetworkElements,
    setPortTypes,
    setTempPortTypeId,
    setNeNetworkElements,
    setFeNetworkElements,
    setNePortsInfo,
    setFePortsInfo,
    resetNePortInfo,
    nePortPrefix,
    noOfLinksToAdd,
    tempPortTypeId,
    resetFePortInfo,
    fePortPrefix,
    resetLinkPortInfo,
    linkPrefix,
  ]);

  useEffect(() => {
    fetchNetworkElementsAndPortTypes();
    window.history.replaceState({}, document.title);
  }, []);

  const handleBandwidthDescriptionChange = useCallback(
    (e: any) => {
      setBandwidthDescription(e.target.value);
    },
    [setBandwidthDescription]
  );

  const handleFePortDescriptionChange = useCallback(
    (e: any) => {
      setFePortDescription(e.target.value);
    },
    [setFePortDescription]
  );

  const handleNePortDescriptionChange = useCallback(
    (e: any) => {
      setNePortDescription(e.target.value);
    },
    [setNePortDescription]
  );

  const handleNoOfLinksChange = useCallback(
    (e: any) => {
      const noOfLinkToAddFromInput = e.target.value.trim();
      if (noOfLinkToAddFromInput === "") {
        setNoOfLinksToAdd("");
        setNoOfLinksError(ErrorMessages.addBandwidth.noOfLinksRequired);
      } else if (parseInt(noOfLinkToAddFromInput) === 0) {
        setNoOfLinksToAdd(noOfLinkToAddFromInput);
        setNoOfLinksError(ErrorMessages.addBandwidth.noOfLinksMinLimit);
      } else if (Regex.numbers.test(noOfLinkToAddFromInput)) {
        setNoOfLinksError("");
        setNoOfLinksToAdd(parseInt(noOfLinkToAddFromInput));

        setLinkPortsInfo(resetLinkPortInfo(linkPrefix, noOfLinkToAddFromInput, tempPortTypeId, true));
        setNePortsInfo(resetNePortInfo(nePortPrefix, noOfLinkToAddFromInput, tempPortTypeId, true));
        setFePortsInfo(resetFePortInfo(fePortPrefix, noOfLinkToAddFromInput, tempPortTypeId, true));
      }
    },
    [
      setNoOfLinksToAdd,
      setNoOfLinksError,
      setNePortsInfo,
      setLinkPortsInfo,
      setFePortsInfo,
      tempPortTypeId,
      resetNePortInfo,
      nePortPrefix,
      resetFePortInfo,
      fePortPrefix,
      resetLinkPortInfo,
      linkPrefix,
    ]
  );

  const handlePortTypeChange = useCallback(
    (portTypeId: number) => {
      const tempPortsInfo = nePortsInfo.map((portInfo) => {
        return {
          ...portInfo,
          portTypeId,
        };
      });
      setNePortsInfo(tempPortsInfo);
      const tempFePortsInfo = fePortsInfo.map((portInfo) => {
        return {
          ...portInfo,
          portTypeId,
        };
      });
      setFePortsInfo(tempFePortsInfo);
      setTempPortTypeId(portTypeId);
    },
    [nePortsInfo, setNePortsInfo, fePortsInfo, setFePortsInfo, setTempPortTypeId]
  );

  const handleNePortNameChange = useCallback(
    (portTempId: number, portName: string) => {
      const tempPortsInfo = nePortsInfo.map((portInfo) => {
        if (portInfo.id === portTempId) {
          return {
            ...portInfo,
            portName,
          };
        }
        return portInfo;
      });
      setNePortsInfo(tempPortsInfo);
    },
    [nePortsInfo, setNePortsInfo]
  );

  const handleFePortNameChange = useCallback(
    (portTempId: number, portName: string) => {
      const tempPortsInfo = fePortsInfo.map((portInfo) => {
        if (portInfo.id === portTempId) {
          return {
            ...portInfo,
            portName,
          };
        }
        return portInfo;
      });
      setFePortsInfo(tempPortsInfo);
    },
    [fePortsInfo, setFePortsInfo]
  );

  const handleLinkPortNameChange = useCallback(
    (portTempId: number, portName: string) => {
      const tempPortsInfo = linkPortsInfo.map((portInfo) => {
        if (portInfo.id === portTempId) {
          return {
            ...portInfo,
            portName,
          };
        }
        return portInfo;
      });
      setLinkPortsInfo(tempPortsInfo);
    },
    [linkPortsInfo, setLinkPortsInfo]
  );
  const handleNePortPrefixChange = useCallback(
    (e: any) => {
      setNePortPrefix(e.target.value || "");
      setNePortsInfo(resetNePortInfo(e.target.value.trim(), noOfLinksToAdd, tempPortTypeId, true));
    },
    [setNePortPrefix, noOfLinksToAdd, resetPortInfo, tempPortTypeId, resetNePortInfo]
  );
  const handleLinkPrefixChange = useCallback(
    (e: any) => {
      setLinkPrefix(e.target.value || "");
      setLinkPortsInfo(resetLinkPortInfo(e.target.value, noOfLinksToAdd, tempPortTypeId, true));
    },
    [resetLinkPortInfo, linkPrefix, noOfLinksToAdd, tempPortTypeId]
  );
  const handleFePortPrefixChange = useCallback(
    (e: any) => {
      setFePortPrefix(e.target.value || "");
      setFePortsInfo(resetFePortInfo(e.target.value.trim(), noOfLinksToAdd, tempPortTypeId, true));
    },
    [setFePortPrefix, noOfLinksToAdd, resetPortInfo, tempPortTypeId, resetFePortInfo]
  );

  const handleFeNetworkElementChange = useCallback(
    (value: any) => {
      const selectedNE = feNetworkElements.find((item) => item.ne_name === value) || "";
      setSelectedFeNetworkElement(selectedNE);
      setFeInput(selectedNE.ne_name);
      setNeNetworkElements(networkElements.filter((item: any) => item.ne_name !== value));
      feUseGroupProps.GroupDropdownProps.reset();
    },
    [networkElements, setFeInput, feNetworkElements, setSelectedFeNetworkElement, setNeNetworkElements, feUseGroupProps]
  );

  const handleNeNetworkElementChange = useCallback(
    (value: any) => {
      const selectedNE = neNetworkElements.find((item) => item.ne_name === value) || "";
      setSelectedNeNetworkElement(selectedNE);
      setNeInput(selectedNE.ne_name);
      setFeNetworkElements(networkElements.filter((item: any) => item.ne_name !== value));
      neUseGroupProps.GroupDropdownProps.reset();
    },
    [networkElements, neNetworkElements, setNeInput, setSelectedNeNetworkElement, setFeNetworkElements, neUseGroupProps]
  );

  const handleFEInputChange = useCallback(
    (inputValue: string) => {
      setFeInput(inputValue);
    },
    [setFeInput]
  );

  const handleNEInputChange = useCallback(
    (inputValue: string) => {
      setNeInput(inputValue);
    },
    [setNeInput]
  );

  const handleSwipeClick = useCallback(() => {
    setNeNetworkElements(feNetworkElements);
    setFeNetworkElements(neNetworkElements);
    setFePortDescription(nePortDescription);
    setNePortDescription(fePortDescription);
    setFePortsInfo(nePortsInfo);
    setNePortsInfo(fePortsInfo);
    setSelectedNeNetworkElement(selectedFeNetworkElement);
    setSelectedFeNetworkElement(selectedNeNetworkElement);
  }, [
    neNetworkElements,
    feNetworkElements,
    fePortsInfo,
    nePortsInfo,
    fePortDescription,
    nePortDescription,
    selectedFeNetworkElement,
    selectedNeNetworkElement,
    setSelectedFeNetworkElement,
    setSelectedNeNetworkElement,
    setNeNetworkElements,
    setFeNetworkElements,
    setFePortDescription,
    setNePortDescription,
    setFePortsInfo,
    setNePortsInfo,
  ]);

  const handleAddBandwidthClick = useCallback(() => {
    setAddBandwidthLoading(true);

    const payload = {
      bandwidthDescription: bandwidthDescription.trim(),
      nePortsInfo: {
        neId: Number(selectedNeNetworkElement.id),
        nodeId: Number(selectedNeNetworkElement.node_id),
        groupId: Number(neUseGroupProps.GroupDropdownProps.groupId),
        groupName: neUseGroupProps.GroupDropdownProps.groupName,
        // portDescription: nePortDescription.trim(),
        reqVerifyNE: verifyNearendPorts,
        portDetails: nePortsInfo.map((portInfo, index) => {
          return {
            ...portInfo,
            portName: portInfo.portName.trim(),
            bandwidthName: linkPortsInfo[index].portName.trim(),
          };
        }),
      },
      fePortsInfo: {
        neId: selectedFeNetworkElement.id,
        nodeId: selectedFeNetworkElement.node_id,
        groupId: Number(feUseGroupProps.GroupDropdownProps.groupId),
        // portDescription: fePortDescription.trim(),
        groupName: feUseGroupProps.GroupDropdownProps.groupName,
        reqVerifyFE: verifyFarendPorts,
        portDetails: fePortsInfo.map((portInfo, index) => {
          return {
            ...portInfo,
            portName: portInfo.portName.trim(),
            bandwidthName: linkPortsInfo[index].portName.trim(),
          };
        }),
      },
    };

    AddPortService.addBandwidth(payload)
      .then(() => {
        if (!isMounted) return;
        setAddBandwidthLoading(false);
        setNoOfLinksApiError("");
        let successMessage = SuccessMessage.addBandwidth.replace("NE1", selectedNeNetworkElement.ne_name);
        successMessage = successMessage.replace("NE2", selectedFeNetworkElement.ne_name);
        toast.success(successMessage, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        navigate(AuthRoutes.manageBandwidth);
      })
      .catch((e: any) => {
        if (!isMounted) return;
        setAddBandwidthLoading(false);
        if (e.errorCode === ErrorCodesMapping[1006]) {
          setNoOfLinksApiError(ErrorCodeMessageMapping[1008]);
        } else if (e.errorCode === ErrorCodesMapping[1007]) {
          toast.error(ErrorCodeMessageMapping[1009], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setNoOfLinksApiError("");
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
      });
  }, [
    bandwidthDescription,
    selectedNeNetworkElement.id,
    selectedNeNetworkElement.node_id,
    selectedNeNetworkElement.ne_name,
    nePortDescription,
    verifyNearendPorts,
    nePortsInfo,
    selectedFeNetworkElement.id,
    selectedFeNetworkElement.node_id,
    selectedFeNetworkElement.ne_name,
    fePortDescription,
    verifyFarendPorts,
    fePortsInfo,
    neUseGroupProps.GroupDropdownProps.groupId,
    feUseGroupProps.GroupDropdownProps.groupId,
    neUseGroupProps.GroupDropdownProps.groupName,
    feUseGroupProps.GroupDropdownProps.groupName,
    navigate,
  ]);

  const handleNEVerifyChange = useCallback(() => {
    setVerifyNearendPorts(!verifyNearendPorts);
  }, [setVerifyNearendPorts, verifyNearendPorts]);

  const handleFEVerifyChange = useCallback(() => {
    setVerifyFarendPorts(!verifyFarendPorts);
  }, [setVerifyFarendPorts, verifyFarendPorts]);

  const addBandwidthDisabled =
    !selectedFeNetworkElement.id ||
    !selectedNeNetworkElement.id ||
    !!(
      noOfLinksError ||
      !neUseGroupProps.GroupDropdownProps.groupName ||
      !neUseGroupProps.GroupDropdownProps.groupName.trim() ||
      !feUseGroupProps.GroupDropdownProps.groupName ||
      !feUseGroupProps.GroupDropdownProps.groupName.trim() ||
      nePortsInfo.find((portInfo) => !portInfo.portName.trim() || !neUseGroupProps.GroupDropdownProps.IsPortNameValid(portInfo.portName.trim())) ||
      fePortsInfo.find((portInfo) => !portInfo.portName.trim() || !feUseGroupProps.GroupDropdownProps.IsPortNameValid(portInfo.portName.trim()))
    );

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter" && !addBandwidthDisabled) {
        handleAddBandwidthClick();
      }
    },
    [handleAddBandwidthClick, addBandwidthDisabled]
  );

  return (
    <div className="form-root">
      <div className="form-main">
        <AddBandwidthForm
          bandwidthDescription={bandwidthDescription}
          fePortDescription={fePortDescription}
          nePortDescription={nePortDescription}
          noOfLinksError={noOfLinksError}
          noOfLinksApiError={noOfLinksApiError}
          noOfLinksToAdd={noOfLinksToAdd}
          neNetworkElementOptions={neNetworkElements}
          feNetworkElementOptions={feNetworkElements}
          selectedNeNetworkElement={selectedNeNetworkElement}
          selectedFeNetworkElement={selectedFeNetworkElement}
          portTypeOptions={portTypes}
          nePortsInfo={nePortsInfo}
          fePortsInfo={fePortsInfo}
          linkPortsInfo={linkPortsInfo}
          tempPortTypeId={tempPortTypeId}
          verifyNearendPorts={verifyNearendPorts}
          verifyFarendPorts={verifyFarendPorts}
          onNeNetworkElementChange={handleNeNetworkElementChange}
          onFeNetworkElementChange={handleFeNetworkElementChange}
          onBandwidthDescriptionChange={handleBandwidthDescriptionChange}
          onFePortDescriptionChange={handleFePortDescriptionChange}
          onNePortDescriptionChange={handleNePortDescriptionChange}
          onNoOfLinksChange={handleNoOfLinksChange}
          onPortTypeChange={handlePortTypeChange}
          onNePortNameChange={handleNePortNameChange}
          onFePortNameChange={handleFePortNameChange}
          onLinkPortNameChange={handleLinkPortNameChange}
          onKeypress={handleKeypress}
          onSwipeClick={handleSwipeClick}
          handleFEVerifyChange={handleFEVerifyChange}
          handleNEVerifyChange={handleNEVerifyChange}
          neInput={neInput}
          feInput={feInput}
          handleNEInputChange={handleNEInputChange}
          handleFEInputChange={handleFEInputChange}
          neUseGroupProps={neUseGroupProps}
          feUseGroupProps={feUseGroupProps}
          nePortPrefix={nePortPrefix}
          fePortPrefix={fePortPrefix}
          handleNePortPrefixChange={handleNePortPrefixChange}
          handleFePortPrefixChange={handleFePortPrefixChange}
          linkPrefix={linkPrefix}
          handleLinkPrefixChange={handleLinkPrefixChange}
        />
      </div>
      <div className="btn-root">
        <Link to={AuthRoutes.manageBandwidth} className="cancel-btn-link">
          <Button variant="greyed" className="btn cancel-btn" node="Cancel" />
        </Link>
        <Button
          variant="primary"
          className="btn"
          node="Add Link"
          loading={addBandwidthLoading}
          disabled={addBandwidthDisabled}
          onClick={handleAddBandwidthClick}
        />
      </div>
      {neUseGroupProps.GroupDrawerProps.drawerComponent}
      {feUseGroupProps.GroupDrawerProps.drawerComponent}
    </div>
  );
};

export default AddBandwidth;
